/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React from 'react';
import type { IconViewProps } from '@airasia-phoenix/react-native/components/IconView';
import type { TextViewProps } from '@airasia-phoenix/react-native/components/TextView';
import {
  getColor,
  getTextStyle,
  pd,
} from '@airasia-phoenix/react-native/utils/utils';
import type { TFunction } from 'i18next';
import Check from '@move-travel/react-native-phoenix-icons/Check';
import countryList from '../../utils/countryList.json';
import {
  CountryData,
  groupCountriesByAlphabet,
} from '../../utils/mapCountries';
import { GENERAL_TERM_URLS } from '../../utils/signupLoginUtils';

/** Required-field message when value is empty/whitespace. */
export function getRequiredFieldError(value: string, t: TFunction): string {
  return value.trim() ? '' : t('signUpFormView.fieldRequired');
}

/** Success checkmark shown when a text field is filled, unfocused, and valid. */
export function getFilledFieldEndIcon(
  value: string,
  errorMessage: string,
  isFocused: boolean,
): IconViewProps | undefined {
  if (isFocused || errorMessage || !value.trim()) {
    return undefined;
  }
  return {
    height: 24,
    width: 24,
    tint: getColor(pd.sys.colour.sentiment_success),
    children: <Check />,
  };
}

/** Parental-consent copy with Privacy Statement link. */
export function buildParentalConsentLabel(
  t: TFunction,
  onPrivacyPress: () => void,
): TextViewProps {
  const privacyLinkLabel = t('signUpFormView.parentalConsentPrivacyLink');
  const consentTypescale = pd.sys.typescale.sub_body_text.lineHeight(18);
  const consentLinkTypescale =
    pd.sys.typescale.sub_body_text_medium.lineHeight(18);

  return {
    text: t('signUpFormView.parentalConsentMessage'),
    style: getTextStyle(consentTypescale, pd.sys.colour.content_secondary),
    spannableTextsProps: [
      {
        spannable: privacyLinkLabel,
        highlightColor: pd.sys.colour.interactive_link.hex,
        style: getTextStyle(
          consentLinkTypescale,
          pd.sys.colour.interactive_link,
        ),
        onPress: onPrivacyPress,
      },
    ],
  };
}

export function getGroupedCountryData(countrySearch: string) {
  return groupCountriesByAlphabet(
    countryList.countries as CountryData[],
    countrySearch,
  );
}

export function getCountryIndexValues(
  filteredCountryData: ReturnType<typeof getGroupedCountryData>,
): string[] {
  return filteredCountryData.map((section) =>
    section.title === 'Popular' ? '#' : section.title,
  );
}

export { GENERAL_TERM_URLS };
